import { z } from "zod";
import { listQuerySchema } from "./pagination";

export const CLIENT_NAME_MAX = 200;

export const clientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The URL identity — derived and stored server-side; `id` remains the storage identity. */
  slug: z.string(),
  active: z.boolean(),
  version: z.number().int(),
  /** Derived server-side over open, non-deleted opportunities. */
  openOpportunities: z.number().int(),
  openPipelineValue: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Client = z.infer<typeof clientSchema>;

export const clientNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a client name")
  .max(CLIENT_NAME_MAX, `Use at most ${CLIENT_NAME_MAX} characters`);

export const createClientSchema = z.object({ name: clientNameSchema });
export type CreateClientInput = z.infer<typeof createClientSchema>;

/** Rename carries the version it was read at — a rename is a write like any other. */
export const renameClientSchema = z.object({
  name: clientNameSchema,
  version: z.number().int(),
});
export type RenameClientInput = z.infer<typeof renameClientSchema>;

export const clientsQuerySchema = listQuerySchema.extend({
  active: z.enum(["true", "false"]).optional(),
});

export const CLIENT_SORT_FIELDS = ["name", "active", "openPipelineValue"] as const;

/**
 * The duplicate check **warns, never blocks** — a genuinely similar name must remain
 * creatable. Naming the matches is the point: the user recognises "Acme s.r.o." and
 * picks it rather than creating a second Acme.
 */
export const checkClientNameResponseSchema = z.object({
  matches: z.array(z.object({ id: z.string().uuid(), name: z.string(), active: z.boolean() })),
});

export type CheckClientNameResponse = z.infer<typeof checkClientNameResponseSchema>;
